"use client";
import { useCallback, useEffect, useRef, useState } from "react";
import { Button } from "../../ui/button";
import { Circle, CheckCircle } from "@phosphor-icons/react";
import {
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from "@/components/ui/accordion";

import { cn } from "@/lib/utils";

/**
 * Extra classes for competition entry-tag pills (`variant="default"`).
 * Colours/radius/border come from globals → current theme (“Button Settings” in the CMS).
 * Unselected: solid fill; hover inverts to an outlined pill on lg+ breakpoints.
 * Selected: stays outlined like that hover appearance (no extra ring/outline flash).
 */
export function competitionTagFilterButtonClassName(isSelected) {
  return cn(
    "lg:hover:bg-transparent lg:hover:border-button-width lg:hover:border-button lg:hover:text-button",
    isSelected &&
      "bg-transparent border-button-width border-button text-button"
  );
}

function FilterCheckboxList({ items, selected, onToggle, fillHeight = false }) {
  const listRef = useRef(null);
  const [canScrollMore, setCanScrollMore] = useState(false);

  const updateScrollFade = useCallback(() => {
    const el = listRef.current;
    if (!el) return;
    const remaining = el.scrollHeight - el.scrollTop - el.clientHeight;
    setCanScrollMore(remaining > 4);
  }, []);

  useEffect(() => {
    const el = listRef.current;
    if (!el) return;

    updateScrollFade();

    const observer = new ResizeObserver(updateScrollFade);
    observer.observe(el);
    window.addEventListener("resize", updateScrollFade);

    return () => {
      observer.disconnect();
      window.removeEventListener("resize", updateScrollFade);
    };
  }, [items, fillHeight, updateScrollFade]);

  return (
    <div
      ref={listRef}
      onScroll={updateScrollFade}
      className={cn(
        "w-full space-y-3 overflow-y-auto overscroll-contain pr-1",
        fillHeight
          ? "min-h-0 flex-1"
          : "max-h-[min(50vh,22rem)]",
        // Soft bottom fade when more options are below (works on any panel colour)
        canScrollMore &&
          "[mask-image:linear-gradient(to_bottom,#000_0%,#000_calc(100%-2.75rem),transparent_100%)] [-webkit-mask-image:linear-gradient(to_bottom,#000_0%,#000_calc(100%-2.75rem),transparent_100%)]"
      )}
    >
      {items.map((item, index) => (
        <div
          key={index}
          className="flex items-center justify-between w-full space-x-2"
        >
          <label
            className="flex space-x-2 cursor-pointer"
            htmlFor={`${item.toLowerCase()}`}
            onClick={() => onToggle(item)}
          >
            {selected.includes(item) ? (
              <CheckCircle size={24} weight="fill" />
            ) : (
              <Circle size={24} />
            )}
            <span>{item}</span>
          </label>
        </div>
      ))}
    </div>
  );
}

export function FilterNav({
  items,
  setActiveItems,
  activeItems,
  filterName,
  asCheckbox = false,
  /** When true with asCheckbox: static title + list (no collapse control) */
  alwaysOpen = false,
  /**
   * When true with asCheckbox: open section fills remaining modal height and
   * scrolls internally so sibling accordion triggers stay on-screen.
   * Parent Accordion must be `flex flex-1 flex-col min-h-0 overflow-hidden`.
   */
  containOpen = false,
  singleSelect = false,
  competitionTagStyle = false,
}) {
  const selected = activeItems ?? [];

  function toggleItem(item) {
    if (singleSelect) {
      setActiveItems(selected.includes(item) ? [] : [item]);
      return;
    }
    setActiveItems(
      selected.includes(item)
        ? selected.filter((x) => x !== item)
        : [...selected, item]
    );
  }

  if (items.length < 1) return null;

  const checkboxList = (
    <FilterCheckboxList
      items={items}
      selected={selected}
      onToggle={toggleItem}
      fillHeight={containOpen}
    />
  );

  // Single-section filter modals: match accordion typography, stay open.
  // AccordionTrigger sits in an h3 (uppercase via type.sass) with level-4.
  if (asCheckbox && alwaysOpen) {
    return (
      <div className="flex flex-col w-full min-h-0 flex-1 overflow-hidden border-b-2 border-brand-fawn">
        <h3 className="flex shrink-0 items-center py-5 leading-none level-4">
          {filterName}
        </h3>
        {/* Match AccordionContent text-sm; fill remaining modal height when parent is flex */}
        <div className="flex flex-1 flex-col pb-2 text-sm min-h-0 overflow-hidden">
          <FilterCheckboxList
            items={items}
            selected={selected}
            onToggle={toggleItem}
            fillHeight
          />
        </div>
      </div>
    );
  }

  // Render as AccordionItem only — parent must wrap siblings in one
  // <Accordion type="single" collapsible> so only one section opens at a time.
  if (asCheckbox) {
    return (
      <AccordionItem
        value={filterName}
        className={cn(
          "border-b-2 border-brand-fawn",
          containOpen &&
            "flex flex-col shrink-0 data-[state=open]:min-h-0 data-[state=open]:flex-1 data-[state=open]:shrink data-[state=open]:overflow-hidden"
        )}
      >
        <AccordionTrigger className="items-center py-5 leading-none hover:no-underline">
          {filterName}
        </AccordionTrigger>
        <AccordionContent
          className={cn(
            "pb-2",
            containOpen && "flex h-full min-h-0 flex-col overflow-hidden"
          )}
          containerClassName={
            containOpen
              ? "data-[state=open]:flex-1 data-[state=open]:min-h-0 data-[state=open]:overflow-hidden data-[state=open]:animate-none"
              : undefined
          }
        >
          {checkboxList}
        </AccordionContent>
      </AccordionItem>
    );
  }

  return (
    <div className="hidden lg:block">
      {/* <p>{`Filter by ${filterName}`}</p> */}
      <div className="flex flex-wrap gap-2 py-2">
        {items.map((item, index) => (
          <Button
            size="sm"
            variant={competitionTagStyle ? "default" : "outline"}
            key={index}
            className={
              competitionTagStyle
                ? competitionTagFilterButtonClassName(selected.includes(item))
                : selected.includes(item)
                  ? "bg-button text-button-foreground"
                  : ""
            }
            onClick={() => toggleItem(item)}
          >
            {item}
          </Button>
        ))}
      </div>
    </div>
  );
}
